import { encodeAbiParameters, keccak256, sha256, toBytes, toHex, type Hex } from 'viem';

export interface V2Utf8PromptChunk {
  readonly index: number;
  readonly data: Hex;
  readonly proof: readonly Hex[];
}

export interface V2Utf8PromptPlan {
  readonly encoding: 0;
  readonly encodedBase64: string;
  readonly chunkCount: number;
  readonly root: Hex;
  readonly encodedDigest: Hex;
  readonly decodedDigest: Hex;
  readonly encodedBytes: number;
  readonly decodedBytes: number;
  readonly chunks: readonly V2Utf8PromptChunk[];
}

const EMPTY_DIGEST: Hex = '0xc5d2460186f7233c927e7db2dcc703c0e500b653ca82273b7bfad8045d85a470';
const ZERO32: Hex = `0x${'00'.repeat(32)}`;
const CHUNK_TYPEHASH = keccak256(toBytes('BotnetRegistryV2PromptChunk(uint32 index,bytes32 dataHash,uint32 dataBytes)'));
const EMPTY_CHUNK_TYPEHASH = keccak256(toBytes('BotnetRegistryV2EmptyPromptChunk(uint32 index)'));
const SEQUENCE_TYPEHASH = keccak256(toBytes('BotnetRegistryV2PromptSequence(uint8 encoding,uint32 count)'));
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

type InternalChunk = { readonly index: number; readonly bytes: Uint8Array; readonly leaf: Hex };

function invalidPrompt(): never {
  throw new TypeError('V2_PROMPT_INVALID');
}

function isWellFormedText(text: string): boolean {
  if (text.startsWith('\uFEFF')) return false;
  for (let index = 0; index < text.length; index++) {
    const unit = text.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = text.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
      index++;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return false;
    }
  }
  return true;
}

function hashChunkLeaf(index: number, data: Uint8Array): Hex {
  const dataHash = keccak256(toHex(data));
  return keccak256(encodeAbiParameters(
    [{ type: 'bytes32' }, { type: 'uint32' }, { type: 'bytes32' }, { type: 'uint32' }],
    [CHUNK_TYPEHASH, index, dataHash, data.length],
  ));
}

function indexedEmptyLeaf(index: number): Hex {
  return keccak256(encodeAbiParameters(
    [{ type: 'bytes32' }, { type: 'uint32' }],
    [EMPTY_CHUNK_TYPEHASH, index],
  ));
}

function hashPair(left: Hex, right: Hex): Hex {
  return keccak256(encodeAbiParameters(
    [{ type: 'bytes32' }, { type: 'bytes32' }],
    [left, right],
  ));
}

function buildProofs(leaves: readonly Hex[], count: number): { root: Hex; proofs: Hex[][] } {
  if (count === 0) return { root: ZERO32, proofs: [] };
  let width = 1;
  while (width < count) width <<= 1;
  const bottom = Array.from({ length: width }, (_, index) => index < count ? leaves[index]! : indexedEmptyLeaf(index));
  const levels: Hex[][] = [bottom];
  while (levels[levels.length - 1]!.length > 1) {
    const current = levels[levels.length - 1]!;
    const next: Hex[] = [];
    for (let index = 0; index < current.length; index += 2) next.push(hashPair(current[index]!, current[index + 1]!));
    levels.push(next);
  }
  const proofs = Array.from({ length: count }, (_, leafIndex) => {
    let position = leafIndex;
    const proof: Hex[] = [];
    for (let level = 0; level < levels.length - 1; level++) {
      proof.push(levels[level]![position ^ 1]!);
      position >>= 1;
    }
    return proof;
  });
  return { root: levels[levels.length - 1]![0]!, proofs };
}

function toBase64(data: Uint8Array): string {
  let output = '';
  for (let offset = 0; offset < data.length; offset += 3) {
    const first = data[offset]!;
    const hasSecond = offset + 1 < data.length;
    const hasThird = offset + 2 < data.length;
    const second = hasSecond ? data[offset + 1]! : 0;
    const third = hasThird ? data[offset + 2]! : 0;
    output += BASE64_ALPHABET[first >> 2];
    output += BASE64_ALPHABET[((first & 0x03) << 4) | (second >> 4)];
    output += hasSecond ? BASE64_ALPHABET[((second & 0x0f) << 2) | (third >> 6)] : '=';
    output += hasThird ? BASE64_ALPHABET[third & 0x3f] : '=';
  }
  return output;
}

function sequenceDigest(chunks: readonly InternalChunk[]): Hex {
  let digest = keccak256(encodeAbiParameters(
    [{ type: 'bytes32' }, { type: 'uint8' }, { type: 'uint32' }],
    [SEQUENCE_TYPEHASH, 0, chunks.length],
  ));
  for (const chunk of chunks) {
    digest = keccak256(encodeAbiParameters(
      [{ type: 'bytes32' }, { type: 'uint32' }, { type: 'uint32' }, { type: 'bytes32' }],
      [digest, chunk.index, chunk.bytes.length, keccak256(toHex(chunk.bytes))],
    ));
  }
  return digest;
}

export function encodeV2Utf8Prompt(text: string): V2Utf8PromptPlan {
  if (typeof text !== 'string' || !isWellFormedText(text)) invalidPrompt();
  const joinedBytes = new TextEncoder().encode(text);
  if (joinedBytes.length > 64_000) invalidPrompt();

  const internalChunks: InternalChunk[] = [];
  for (let offset = 0, index = 0; offset < joinedBytes.length; offset += 4096, index++) {
    const chunkBytes = joinedBytes.slice(offset, Math.min(joinedBytes.length, offset + 4096));
    internalChunks.push({ index, bytes: chunkBytes, leaf: hashChunkLeaf(index, chunkBytes) });
  }

  if (internalChunks.length === 0) {
    return Object.freeze({
      encoding: 0 as const,
      encodedBase64: '',
      chunkCount: 0,
      root: ZERO32,
      encodedDigest: EMPTY_DIGEST,
      decodedDigest: EMPTY_DIGEST,
      encodedBytes: 0,
      decodedBytes: 0,
      chunks: Object.freeze([] as V2Utf8PromptChunk[]),
    });
  }

  const { root, proofs } = buildProofs(internalChunks.map(chunk => chunk.leaf), internalChunks.length);
  const publicChunks = internalChunks.map((chunk, index) => Object.freeze({
    index: chunk.index,
    data: toHex(chunk.bytes),
    proof: Object.freeze(proofs[index]!),
  }));
  return Object.freeze({
    encoding: 0 as const,
    encodedBase64: toBase64(joinedBytes),
    chunkCount: internalChunks.length,
    root,
    encodedDigest: sequenceDigest(internalChunks),
    decodedDigest: sha256(toHex(joinedBytes)),
    encodedBytes: joinedBytes.length,
    decodedBytes: joinedBytes.length,
    chunks: Object.freeze(publicChunks),
  });
}
